// wizardSteps.ts — which wizard steps apply to a product type, and how to
// move between them.
//
// Extracted from AdminProductWizard.tsx so the regression guard that matters
// can be asserted rather than argued: for `photogrammetry` these functions are
// the IDENTITY (n+1 / n-1), which is exactly what the hardcoded setStep calls
// did before the fork existed. That is the evidence behind M5b's "byte-identical
// behaviour to today" acceptance criterion and Non-negotiable 9.

export type ProductType = "photogrammetry" | "parametric";

export const TOTAL_STEPS = 7;

/**
 * Steps that collect nothing for a generated product:
 *   3 Media Assets      — a pergola has no photography behind it
 *   5 Configuration     — finishes come from the swatch library (M4), not the
 *                         relational variant_groups/variant_options tables
 *   6 Photos            — same as 3
 *
 * Stepping over them beats rendering them empty: an empty upload panel reads
 * as a missing asset someone ought to go and find.
 */
export const PARAMETRIC_SKIPPED_STEPS = [3, 5, 6];

export function isStepApplicable(step: number, type: ProductType): boolean {
  return type !== "parametric" || !PARAMETRIC_SKIPPED_STEPS.includes(step);
}

/** The next applicable step, clamped to the last step. */
export function nextStepFrom(step: number, type: ProductType): number {
  let next = step + 1;
  while (next < TOTAL_STEPS && !isStepApplicable(next, type)) next++;
  return Math.min(next, TOTAL_STEPS);
}

/** The previous applicable step, clamped to the first step. */
export function prevStepFrom(step: number, type: ProductType): number {
  let prev = step - 1;
  while (prev > 1 && !isStepApplicable(prev, type)) prev--;
  return Math.max(prev, 1);
}
